'use client'

import { useEffect, useState } from 'react'
import {
  hasConsentDecision,
  grantConsent,
  denyConsent,
  getRegionPolicy,
  type RegionPolicy,
} from '@/lib/consent-manager'
import { recordConsentMetric } from '@/lib/consent-metrics'

/**
 * Policy-aware banner copy. Two variants, same visual design:
 * - prior-consent (EEA/UK/CH or unknown region): the original opt-in ask —
 *   nothing runs until Accept.
 * - notice-opt-out (rest of world): analytics runs by default, so the copy
 *   must say so truthfully and offer an opt-out — no "Accept"/"Reject"
 *   framing for a decision that is actually notice + opt-out.
 */
type ConsentLocale = 'en' | 'ar'

type ConsentCopy = {
  intro: string
  message: string
  learnMore: string
  dialogLabel: string
  primaryLabel: string
  primaryAria: string
  secondaryLabel: string
  secondaryAria: string
}

const BANNER_COPY: Record<ConsentLocale, Record<RegionPolicy, ConsentCopy>> = {
  en: {
    'prior-consent': {
      intro: 'We value your privacy.',
      message:
        'We use cookies and similar technologies to analyze site usage and improve your experience. You can choose to accept or decline analytics tracking.',
      learnMore: 'Learn more',
      dialogLabel: 'Cookie consent banner',
      primaryLabel: 'Accept All',
      primaryAria: 'Accept all analytics cookies',
      secondaryLabel: 'Reject All',
      secondaryAria: 'Reject all analytics cookies',
    },
    'notice-opt-out': {
      intro: 'We value your privacy.',
      message:
        'We use analytics cookies to understand site usage and improve your experience. Analytics is active by default in your region — you can opt out at any time.',
      learnMore: 'Learn more',
      dialogLabel: 'Cookie consent banner',
      primaryLabel: 'Got It',
      primaryAria: 'Acknowledge analytics notice',
      secondaryLabel: 'Opt Out',
      secondaryAria: 'Opt out of analytics cookies',
    },
  },
  ar: {
    'prior-consent': {
      intro: 'نحترم خصوصيتك.',
      message:
        'نستخدم ملفات تعريف الارتباط وتقنيات مشابهة لتحليل استخدام الموقع وتحسين تجربتك. يمكنك قبول تتبع التحليلات أو رفضه.',
      learnMore: 'اعرف المزيد',
      dialogLabel: 'إعدادات ملفات تعريف الارتباط',
      primaryLabel: 'قبول الكل',
      primaryAria: 'قبول جميع ملفات تعريف الارتباط التحليلية',
      secondaryLabel: 'رفض الكل',
      secondaryAria: 'رفض جميع ملفات تعريف الارتباط التحليلية',
    },
    'notice-opt-out': {
      intro: 'نحترم خصوصيتك.',
      message:
        'نستخدم ملفات تعريف الارتباط التحليلية لفهم استخدام الموقع وتحسين تجربتك. التحليلات مفعّلة افتراضيًا في منطقتك، ويمكنك إيقافها في أي وقت.',
      learnMore: 'اعرف المزيد',
      dialogLabel: 'إعدادات ملفات تعريف الارتباط',
      primaryLabel: 'حسنًا',
      primaryAria: 'الإقرار بإشعار التحليلات',
      secondaryLabel: 'إيقاف التحليلات',
      secondaryAria: 'إيقاف ملفات تعريف الارتباط التحليلية',
    },
  },
}

export default function CookieConsent({
  locale = 'en',
}: {
  locale?: ConsentLocale
}) {
  const [showBanner, setShowBanner] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [policy, setPolicy] = useState<RegionPolicy>('prior-consent')

  useEffect(() => {
    setPolicy(getRegionPolicy())

    // Check if user has already made a decision
    const hasDecision = hasConsentDecision()

    let showTimer: ReturnType<typeof setTimeout> | undefined
    if (!hasDecision) {
      // Short delay only to avoid hydration/layout jank — long enough for
      // paint, short enough that bounce traffic still sees the banner.
      showTimer = setTimeout(() => {
        setShowBanner(true)
        recordConsentMetric('banner_shown')
        // Trigger fade-in animation
        setTimeout(() => setIsVisible(true), 50)
      }, 200)
    }

    // Reopen on request (footer "Cookie Settings" link) even after a decision
    const handleReopen = () => {
      setShowBanner(true)
      setTimeout(() => setIsVisible(true), 50)
    }
    window.addEventListener('pm-consent-reopen', handleReopen)
    return () => {
      if (showTimer) clearTimeout(showTimer)
      window.removeEventListener('pm-consent-reopen', handleReopen)
    }
  }, [])

  // Announce visibility so bottom-anchored overlays (AskShell) can lift
  // themselves above the banner while it is on screen.
  useEffect(() => {
    window.dispatchEvent(new Event('pm-consent-banner-change'))
    return () => {
      // Covers unmount (showBanner -> false) as well
      window.dispatchEvent(new Event('pm-consent-banner-change'))
    }
  }, [isVisible, showBanner])

  const handleAccept = () => {
    // A later change via "Cookie Settings" is a preference change, not a
    // first decision — read before the new record is written.
    const hadDecision = hasConsentDecision()
    // grantConsent writes the v2 record + pm_consent_v2 mirror. It never
    // throws (storage failures fall back to in-memory state), so the banner
    // always completes and analytics always initializes.
    grantConsent(policy === 'notice-opt-out' ? 'notice-ack' : 'banner-accept')
    recordConsentMetric(hadDecision ? 'preference_changed' : 'accepted')
    try {
      window.dispatchEvent(new Event('pm-consent-accepted'))
      // Redundant but harmless: ensure GA flips if GTM shim is present
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        })
      }
    } catch {}
    setIsVisible(false)
    setTimeout(() => setShowBanner(false), 300)
  }

  const handleDecline = () => {
    const hadDecision = hasConsentDecision()
    // denyConsent writes the v2 record + pm_consent_v2 mirror (never throws;
    // in-memory fallback keeps the denial effective for this session).
    denyConsent(
      policy === 'notice-opt-out' ? 'notice-opt-out' : 'banner-decline'
    )
    recordConsentMetric(
      hadDecision
        ? 'preference_changed'
        : policy === 'notice-opt-out'
          ? 'opted_out'
          : 'rejected'
    )
    try {
      window.dispatchEvent(new Event('pm-consent-declined'))
      // Reinforce Consent Mode hard deny for GTM/GA4
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        })
      }
    } catch {}
    setIsVisible(false)
    setTimeout(() => setShowBanner(false), 300)
  }

  if (!showBanner) return null

  const copy = BANNER_COPY[locale][policy]

  return (
    <div
      data-consent-banner
      data-visible={isVisible ? 'true' : 'false'}
      lang={locale}
      dir={locale === 'ar' ? 'rtl' : undefined}
      className={`fixed bottom-0 left-0 right-0 z-[1000] transition-all duration-300 ease-in-out motion-reduce:transition-none ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      role='dialog'
      aria-live='polite'
      aria-label={copy.dialogLabel}
    >
      {/* Subtle 1px brand accent along the dock's top edge */}
      <div
        aria-hidden='true'
        className='h-px w-full bg-gradient-to-r from-primary via-cta to-accent opacity-70'
      />
      <div className='bg-card pb-[env(safe-area-inset-bottom)]'>
        <div className='max-w-shell mx-auto px-4 py-4'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6'>
            {/* Message */}
            <div className='flex-1'>
              <p className='text-sm text-muted-foreground leading-relaxed'>
                <span className='font-semibold text-foreground'>
                  {copy.intro}
                </span>{' '}
                {copy.message}{' '}
                <a
                  href='/privacy-policy'
                  hrefLang='en'
                  className='text-accent-blue hover:text-foreground underline underline-offset-2 transition-colors duration-200 motion-reduce:transition-none'
                >
                  {copy.learnMore}
                </a>
              </p>
            </div>

            {/* Actions - outlined secondary, filled primary; equal size and reach */}
            <div className='flex gap-2.5 shrink-0 sm:gap-3'>
              <button
                onClick={handleDecline}
                className='inline-flex h-10 flex-1 items-center justify-center border border-border/70 bg-transparent px-4 text-sm font-medium text-foreground transition-colors duration-200 hover:border-border hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card motion-reduce:transition-none sm:flex-none'
                aria-label={copy.secondaryAria}
              >
                {copy.secondaryLabel}
              </button>
              <button
                onClick={handleAccept}
                className='inline-flex h-10 flex-1 items-center justify-center bg-cta-fill px-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[color-mix(in_srgb,var(--cta-fill)_85%,black)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card motion-reduce:transition-none sm:flex-none'
                aria-label={copy.primaryAria}
              >
                {copy.primaryLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
